import {useEffect, useRef} from 'react'
import {announce} from '@github-ui/aria-live'
import type {RelayCheckpoint} from '@github-ui/ahp-relay/session-checkpoints'
import {completedCheckpoints} from '@github-ui/ahp-relay/session-checkpoints'
import {COMPACTION_MARKER_ANCHOR, describeCompaction, toCompactionMarker} from './compaction-marker'

interface AnnouncedCompactions {
  sessionKey: string | undefined
  checkpointNumbers: Set<number>
}

/**
 * Announces each compaction that completes while the transcript is open, once.
 * The first report per `sessionKey` is a silent baseline, so history isn't
 * re-announced. No `sessionKey` means saved history: nothing is announced.
 */
export function useCompactionAnnouncements(
  sessionKey: string | undefined,
  checkpoints: readonly RelayCheckpoint[] | undefined,
): void {
  const announcedRef = useRef<AnnouncedCompactions | undefined>(undefined)

  useEffect(() => {
    if (sessionKey === undefined || checkpoints === undefined) return
    const completed = completedCheckpoints(checkpoints)
    const announced = announcedRef.current

    if (!announced || announced.sessionKey !== sessionKey) {
      announcedRef.current = {
        sessionKey,
        checkpointNumbers: new Set(completed.map(checkpoint => checkpoint.checkpointNumber)),
      }
      return
    }

    for (const checkpoint of completed) {
      if (announced.checkpointNumbers.has(checkpoint.checkpointNumber)) continue
      announced.checkpointNumbers.add(checkpoint.checkpointNumber)
      const marker = toCompactionMarker(checkpoint, COMPACTION_MARKER_ANCHOR)
      announce(describeCompaction(marker), {assertive: !marker.success})
    }
  }, [sessionKey, checkpoints])
}
